import { EditorConfig } from "@ckeditor/ckeditor5-core";
import { ClassicEditor } from "@ckeditor/ckeditor5-editor-classic";
import {
  Autoformat,
  AutoLink,
  Autosave,
  Bold,
  Essentials,
  Highlight,
  Indent,
  IndentBlock,
  Italic,
  Link,
  LinkEditing,
  List,
  Mention,
  Paragraph,
  Strikethrough,
  TodoList,
  Underline,
  WordCount,
  Undo,
  // AutoImage, // Available but not used yet
  // ImageInsert, // Available but not used yet
} from "./ckeditor-plugins";
import extraPlugins from "./plugins";

export const LICENSE_KEY = "GPL";

// Base plugins - always included
export const BASE_EDITOR_PLUGINS = [
  Autoformat,
  AutoLink,
  Autosave,
  Bold,
  Essentials,
  Highlight,
  Indent,
  IndentBlock,
  Italic,
  LinkEditing,
  List,
  Mention,
  Paragraph,
  Strikethrough,
  TodoList,
  Underline,
  WordCount,
  Undo,
];

// Function to create plugin array with optional extra plugins
export const createEditorPlugins = (includeExtraPlugins: boolean = true) => {
  return includeExtraPlugins 
    ? [...BASE_EDITOR_PLUGINS, ...extraPlugins]
    : BASE_EDITOR_PLUGINS;
};

// Default plugins configuration (with extra plugins enabled by default for backward compatibility)
export const CLASSIC_EDITOR_PLUGINS = createEditorPlugins(true);

// Define default configuration as a separate object
export const CLASSIC_EDITOR_CONFIG: EditorConfig = {
  // Hide the toolbar by providing empty items array
  toolbar: {
    items: [], // Empty toolbar - effectively hidden
    shouldNotGroupWhenFull: false,
  },

  // Image configuration
  image: {
    toolbar: [],
  },

  // License key
  licenseKey: LICENSE_KEY,

  // Link configuration
  link: {
    addTargetToExternalLinks: true,
    defaultProtocol: "https://",
    decorators: {
      toggleDownloadable: {
        mode: "manual",
        label: "Downloadable",
        attributes: {
          download: "file",
        },
      },
    },
  },

  // Mention configuration
  mention: {
    feeds: [
      {
        marker: "@",
        feed: [
          /* See: https://ckeditor.com/docs/ckeditor5/latest/features/mentions.html */
        ],
      },
    ],
  },

  // Placeholder text
  placeholder: "Type or paste your content here!",

  // List configuration
  list: {
    properties: {
      styles: true,
      startIndex: true,
      reversed: true,
    },
  },
};

// Helper: resolve element or treat string as data
function resolveSource(
  source: HTMLElement | string
): { element?: HTMLElement; data?: string } {
  if (typeof source !== "string") {
    return { element: source };
  }

  // Heuristic: if string starts with '<' treat as HTML/data
  if (source.trim().startsWith("<")) {
    return { data: source };
  }

  // Try CSS selector first
  const bySelector = document.querySelector<HTMLElement>(source);
  if (bySelector) return { element: bySelector };

  // Try id lookup (when user passed "editor" instead of "#editor")
  const byId = document.getElementById(source);
  if (byId) return { element: byId };

  // No element found — throw rather than silently treating a bad selector as content.
  throw new Error(
    `[arbo-ckeditor] Could not find element for selector/id "${source}". Pass an HTMLElement, a valid CSS selector, or an HTML string.`
  );
}

// Create a custom editor class WITHOUT modifying the original ClassicEditor
export default class CustomClassicEditor extends ClassicEditor {
  // Define builtin plugins for this specific editor instance
  public static override readonly builtinPlugins: any[] = CLASSIC_EDITOR_PLUGINS;

  // Define default config for this specific editor instance
  public static override readonly defaultConfig = CLASSIC_EDITOR_CONFIG;

  public static override async create(
    sourceElementOrData: HTMLElement | string,
    config?: EditorConfig & { includeExtraPlugins?: boolean, enableLinkPlugin?: boolean }
  ): Promise<CustomClassicEditor> {
    const { includeExtraPlugins = true, enableLinkPlugin = false, ...cleanConfig } = config || {};

    const { element, data } = resolveSource(sourceElementOrData);

    const hasCustomPlugins =
      Array.isArray(cleanConfig.plugins) && (cleanConfig.plugins as any[]).length > 0;

    // Build plugin list, swapping LinkEditing <-> Link based on enableLinkPlugin
    let plugins: any[] = hasCustomPlugins
      ? (cleanConfig.plugins as any[])
      : createEditorPlugins(includeExtraPlugins);

    if (!hasCustomPlugins) {
      plugins = plugins.map((p: any) => {
        if (enableLinkPlugin && p === LinkEditing) return Link;
        if (!enableLinkPlugin && p === Link) return LinkEditing;
        return p;
      });
    }

    // Always pass plugins explicitly via config to avoid mutating the shared static builtinPlugins.
    const mergedConfig: EditorConfig = {
      ...CLASSIC_EDITOR_CONFIG,
      ...cleanConfig,
      plugins,
    };

    return super.create(
      element ?? (data as string),
      mergedConfig
    ) as Promise<CustomClassicEditor>;
  }
}

// Export configuration and plugins separately for flexibility
export {
  CLASSIC_EDITOR_CONFIG as ClassicEditorConfig,
  CLASSIC_EDITOR_PLUGINS as ClassicEditorPlugins,
};

// Also export a convenience function to create the editor
export const createClassicEditor = async (
  element: HTMLElement | string,
  customConfig?: Partial<EditorConfig> & { includeExtraPlugins?: boolean; enableLinkPlugin?: boolean }
): Promise<CustomClassicEditor> => {
  return CustomClassicEditor.create(element, customConfig);
};
